import React, { useContext, useState, useEffect } from "react";
import LazyLoad from "react-lazyload";

import { makeStyles } from "@mui/styles";

// My Files
import { DatabaseContext, UserContext } from "../../context";
import VideoNotificationCard from "./VideoNotificationCard";
import PDFNotificationCard from "./PDFNotificationCard";
import AudioNotificationCard from "./AudioNotificationCard";
import ImageNotificationCard from "./ImageNotificationCard";
import PlaylistNotificationCard from "./PlaylistNotificationCard";
import ProjectNotificationCard from "./ProjectNotificationCard";
import SurveyNotificationCard from "./SurveyNotificationCard";
import TimelineNotificationCard from "./TimelineNotificationCard";
import LinkNotificationCard from "./LinkNotificationCard";
import DesktopAppNotificationCard from "./DesktopAppNotificationCard";
import useHasCurrentDesktopLicence from "../desktopapp/useCurrentDesktopLicence";
import { jsonFromArray } from "../../utility";
import firebase from "../../firebase";
import NoNotificationsCard from "./NoNotificationsCard";
import GenericFileNotificationCard from "./GenericFileNotificationCard";
import Button from "@mui/material/Button";
import NotificationCard from "./NotificationCard";
import {
    compareNotificationsByVisibleDate,
    isNotificationVisible,
    useNotificationClock,
    withLatestDesktopAppNotificationOnly,
    withoutDesktopAppNotifications,
} from "./notificationTiming";

const useStyles = makeStyles((theme) => ({
    "@keyframes feedItemEnter": {
        "0%": {
            opacity: 0,
            transform: "translateY(14px) scale(0.992)",
        },
        "100%": {
            opacity: 1,
            transform: "translateY(0) scale(1)",
        },
    },
    "@keyframes welcomeCardEnter": {
        "0%": {
            opacity: 0,
            transform: "translateY(10px)",
        },
        "100%": {
            opacity: 1,
            transform: "translateY(0)",
        },
    },
    noNotificationsMessage: {
        paddingTop: 15,
        padding: 10,
        alignSelf: "center",
        color: theme.palette.text.secondary,
    },
    clearAllButton: {
        width: "100%",
    },
    notificationList: {
        display: "grid",
        gap: theme.spacing(2),
    },
    welcomeCard: {
        animation:
            "$welcomeCardEnter 420ms cubic-bezier(0.22, 1, 0.36, 1) both",
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
        },
    },
    notificationItem: {
        animation:
            "$feedItemEnter 440ms cubic-bezier(0.22, 1, 0.36, 1) both",
        willChange: "transform, opacity",
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            willChange: "auto",
        },
    },
}));

export default function UserFeed(props) {
    const classes = useStyles();

    const { notifications, notificationsLoaded, comments } =
        useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [allNotifications, setAllNotifications] = useState([]);
    const [notificationsHaveLoaded, setNotificationsHaveLoaded] =
        useState(false);
    const [visibleCount, setVisibleCount] = useState(50); // start with 50
    const now = useNotificationClock(notifications);
    const hasDesktopLicence = useHasCurrentDesktopLicence(
        currentUserData?.key,
        notifications,
    );
    const firstName = currentUserData?.firstName?.trim();

    useEffect(() => {
        if (!notificationsLoaded || !firstName) {
            setNotificationsHaveLoaded(false);
            setAllNotifications([]);
            return;
        }

        if (currentUserData) {
            let array = [];
            notifications.forEach((n) => {
                n.comments = comments.filter(
                    (c) => c.file === n.file && c.deleted === false
                );
                if (!n.comments) n.comments = [];

                if (isNotificationVisible(n, now)) array.push(n);
            });

            // Nodiant Coder cards only ever appear in the popup of new
            // notifications. Filtered before the seen filter, so an older
            // unseen card can't stand in for a newer one that has been seen.
            array = props.popupFeed
                ? withLatestDesktopAppNotificationOnly(
                      array,
                      now,
                      hasDesktopLicence,
                  )
                : withoutDesktopAppNotifications(array);

            array = array.filter(
                (n) =>
                    !n.seenBy.includes(currentUserData.key) || !props.popupFeed
            );

            array.sort(compareNotificationsByVisibleDate);

            setNotificationsHaveLoaded(true);
            setAllNotifications(array);
        }
    }, [
        currentUserData,
        firstName,
        notifications,
        notificationsLoaded,
        comments,
        now,
        props.popupFeed,
        hasDesktopLicence,
    ]);

    useEffect(() => {
        setVisibleCount(50); // reset when the underlying list changes
    }, [currentUserData, notifications, comments, props.popupFeed]);

    function updateSeenBy(notification) {
        if (currentUserData) {
            let ref = firebase.database().ref("notifications");

            let seenByObject = jsonFromArray(notification.seenBy);
            seenByObject[currentUserData.key] = true;

            ref.child(notification.key).update({ seenBy: seenByObject });
        }
    }

    function handleClearAllNotificationsPress() {
        allNotifications.forEach((notification) => {
            updateSeenBy(notification);
        });
    }

    function handleShowMore() {
        setVisibleCount((prev) => prev + 50);
    }

    const userNotifications = allNotifications.slice(0, visibleCount);

    return (
        <>
            {props.popupFeed &&
                notificationsHaveLoaded &&
                userNotifications.length === 0 && (
                    <p className={classes.noNotificationsMessage}>
                        You don't have any Notifications
                    </p>
                )}
            {!props.popupFeed && firstName && (
                <div className={classes.welcomeCard}>
                    <NoNotificationsCard
                        compact={
                            !notificationsHaveLoaded ||
                            userNotifications.length > 0
                        }
                        firstName={firstName}
                    />
                </div>
            )}
            <>
                {props.popupFeed && userNotifications.length > 0 && (
                    <Button
                        className={classes.clearAllButton}
                        color="primary"
                        onClick={handleClearAllNotificationsPress}
                    >
                        Clear All
                    </Button>
                )}

                <div className={classes.notificationList}>
                    {userNotifications.map((n, index) => (
                        <LazyLoad overflow key={n.key}>
                            <div
                                key={n.key}
                                className={classes.notificationItem}
                                style={{
                                    ...(props.popupFeed
                                        ? { maxWidth: 600 }
                                        : {}),
                                    animationDelay: `${Math.min(
                                        index * 55,
                                        330,
                                    )}ms`,
                                }}
                            >
                                {n.type === "announcement" && (
                                    <NotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                        type="announcement"
                                    />
                                )}
                                {n.type === "file" && (
                                    <GenericFileNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "video" && (
                                    <VideoNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "playlist" && (
                                    <PlaylistNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "project" && (
                                    <ProjectNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "survey" && (
                                    <SurveyNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "timeline" && (
                                    <TimelineNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "pdf" && (
                                    <PDFNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "audio" && (
                                    <AudioNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "image" && (
                                    <ImageNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "link" && (
                                    <LinkNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                                {n.type === "desktopApp" && (
                                    <DesktopAppNotificationCard
                                        updateSeenBy={updateSeenBy}
                                        popupFeed={props.popupFeed}
                                        notification={n}
                                    />
                                )}
                            </div>
                        </LazyLoad>
                    ))}
                </div>

                {/* Show More button only if NOT popupFeed */}
                {!props.popupFeed && visibleCount < allNotifications.length && (
                    <div style={{ textAlign: "center", marginTop: 16 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleShowMore}
                        >
                            Show More
                        </Button>
                    </div>
                )}
            </>
        </>
    );
}
